import React, { useState } from 'react';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Button,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Switch,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
  CircularProgress,
  LinearProgress,
  Slider,
  IconButton,
  Tooltip,
  Badge,
  Avatar,
  Typography,
} from '@mui/material';
import {
  AddOutlined,
  DeleteOutline,
  EditOutlined,
  SaveOutlined,
  CancelOutlined,
  CheckCircleOutline,
  ErrorOutline,
  InfoOutlined,
  WarningAmberOutlined,
  NotificationsOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import { StyledDataGridWrapper } from '@/components/StyledDataGrid';
import { GridColDef } from '@mui/x-data-grid-pro';
import { PageHeader, ContentCard, TabPanel, RequiredLabel } from '@/components/common';
import { ComponentShowcase } from '@/features/developers';
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableBody,
  Table,
  TableCell,
  TableRow,
} from '@/components/StyledTable';
import { FilterableTable, FilterableColumn } from '@/components/FilterableTable';

function DevelopersPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [switchChecked, setSwitchChecked] = useState(true);
  const [checkboxChecked, setCheckboxChecked] = useState(true);
  const [radioValue, setRadioValue] = useState('option1');
  const [sliderValue, setSliderValue] = useState(30);
  const [textFieldValue, setTextFieldValue] = useState('Sample text');
  const [selectValue, setSelectValue] = useState('option1');
  const [expandedTableRows, setExpandedTableRows] = useState<Set<number>>(new Set([1]));

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleToggleTableRow = (id: number) => {
    const newExpanded = new Set(expandedTableRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedTableRows(newExpanded);
  };

  // Sample data for DataGrid
  const sampleRows = [
    { id: 1, name: 'John Doe', email: 'john@example.com', role: 'Admin', status: 'active' },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'User', status: 'active' },
    { id: 3, name: 'Bob Johnson', email: 'bob@example.com', role: 'User', status: 'inactive' },
  ];

  const sampleColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
    { field: 'role', headerName: 'Role', width: 120 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'active' ? 'success' : 'default'}
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
  ];

  // Sample data for FilterableTable
  const filterableTableData = [
    { id: 1, name: 'John Doe', email: 'john@example.com', role: 'Admin', department: 'Engineering', status: 'active', salary: 85000 },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'Manager', department: 'Marketing', status: 'active', salary: 92000 },
    { id: 3, name: 'Bob Johnson', email: 'bob@example.com', role: 'Developer', department: 'Engineering', status: 'inactive', salary: 75000 },
    { id: 4, name: 'Alice Williams', email: 'alice@example.com', role: 'Designer', department: 'Design', status: 'active', salary: 68000 },
    { id: 5, name: 'Charlie Brown', email: 'charlie@example.com', role: 'Developer', department: 'Engineering', status: 'active', salary: 78000 },
    { id: 6, name: 'Diana Prince', email: 'diana@example.com', role: 'Manager', department: 'Sales', status: 'active', salary: 95000 },
    { id: 7, name: 'Eve Davis', email: 'eve@example.com', role: 'Analyst', department: 'Marketing', status: 'inactive', salary: 62000 },
    { id: 8, name: 'Frank Miller', email: 'frank@example.com', role: 'Developer', department: 'Engineering', status: 'active', salary: 82000 },
  ];

  const filterableTableColumns: FilterableColumn[] = [
    { field: 'name', label: 'Name', minWidth: 150 },
    { field: 'email', label: 'Email', minWidth: 200 },
    { field: 'role', label: 'Role', width: 140 },
    { field: 'department', label: 'Department', width: 140 },
    {
      field: 'status',
      label: 'Status',
      width: 120,
      render: (value) => (
        <Chip
          label={value}
          size="small"
          color={value === 'active' ? 'success' : 'default'}
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'salary',
      label: 'Salary',
      width: 120,
      align: 'right',
      render: (value) => (
        <Typography variant="body2" fontWeight={500}>
          ${value?.toLocaleString()}
        </Typography>
      ),
    },
  ];

  return (
    <Container maxWidth={false} sx={{ py: 4, px: 3 }}>
      <PageHeader
        title="For Developers"
        subtitle="Component library, theme configuration, and usage examples for humans and AI assistants"
      />

      <ContentCard>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Buttons" />
            <Tab label="Forms & Inputs" />
            <Tab label="Feedback" />
            <Tab label="Tables & Data" />
            <Tab label="Custom Components" />
            <Tab label="Theme & Colors" />
          </Tabs>
        </Box>

        {/* Buttons Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ px: 3 }}>
            <ComponentShowcase
              title="Button Variants"
              description="Material UI buttons with theme customization. All buttons use primary color (#4CAEB8) by default."
              code={`<Button variant="contained">Contained</Button>
<Button variant="outlined">Outlined</Button>
<Button variant="text">Text</Button>`}
            >
              <Stack direction="row" spacing={2}>
                <Button variant="contained">Contained</Button>
                <Button variant="outlined">Outlined</Button>
                <Button variant="text">Text</Button>
              </Stack>
            </ComponentShowcase>

            <ComponentShowcase
              title="Button Colors"
              description="Buttons with different color variants. Secondary uses orange (#FBB24C)."
              code={`<Button variant="contained" color="primary">Primary</Button>
<Button variant="contained" color="secondary">Secondary</Button>
<Button variant="contained" color="success">Success</Button>
<Button variant="contained" color="error">Error</Button>
<Button variant="contained" color="warning">Warning</Button>
<Button variant="contained" color="info">Info</Button>`}
            >
              <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                <Button variant="contained" color="primary">Primary</Button>
                <Button variant="contained" color="secondary">Secondary</Button>
                <Button variant="contained" color="success">Success</Button>
                <Button variant="contained" color="error">Error</Button>
                <Button variant="contained" color="warning">Warning</Button>
                <Button variant="contained" color="info">Info</Button>
              </Stack>
            </ComponentShowcase>

            <ComponentShowcase
              title="Button Sizes & Icons"
              description="Buttons with different sizes and icon combinations."
              code={`<Button size="small" startIcon={<AddOutlined />}>Small</Button>
<Button size="medium" startIcon={<SaveOutlined />}>Medium</Button>
<Button size="large" endIcon={<DeleteOutline />}>Large</Button>`}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Button size="small" variant="contained" startIcon={<AddOutlined />}>
                  Small
                </Button>
                <Button size="medium" variant="contained" startIcon={<SaveOutlined />}>
                  Medium
                </Button>
                <Button size="large" variant="contained" endIcon={<DeleteOutline />}>
                  Large
                </Button>
              </Stack>
            </ComponentShowcase>

            <ComponentShowcase
              title="Icon Buttons"
              description="Icon buttons for actions with tooltips."
              code={`<Tooltip title="Edit">
  <IconButton color="primary">
    <EditOutlined />
  </IconButton>
</Tooltip>
<Tooltip title="Delete">
  <IconButton color="error">
    <DeleteOutline />
  </IconButton>
</Tooltip>`}
            >
              <Stack direction="row" spacing={1}>
                <Tooltip title="Edit">
                  <IconButton color="primary">
                    <EditOutlined />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton color="error">
                    <DeleteOutline />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Save">
                  <IconButton color="success">
                    <SaveOutlined />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton>
                    <CancelOutlined />
                  </IconButton>
                </Tooltip>
              </Stack>
            </ComponentShowcase>
          </Box>
        </TabPanel>

        {/* Forms & Inputs Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ px: 3 }}>
            <ComponentShowcase
              title="Text Fields"
              description="Standard text input fields with label and placeholder."
              code={`<TextField
  fullWidth
  label="Email"
  placeholder="Enter your email"
  value={textFieldValue}
  onChange={(e) => setTextFieldValue(e.target.value)}
/>
<TextField
  fullWidth
  label="Message"
  multiline
  rows={3}
  placeholder="Enter your message"
/>`}
            >
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Email"
                  placeholder="Enter your email"
                  value={textFieldValue}
                  onChange={(e) => setTextFieldValue(e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Message"
                  multiline
                  rows={3}
                  placeholder="Enter your message"
                />
              </Stack>
            </ComponentShowcase>

            <ComponentShowcase
              title="Select / Dropdown"
              description="Dropdown menu with options. Hover shows primary color border."
              code={`<FormControl fullWidth>
  <InputLabel>Choose Option</InputLabel>
  <Select
    value={selectValue}
    label="Choose Option"
    onChange={(e) => setSelectValue(e.target.value)}
  >
    <MenuItem value="option1">Option 1</MenuItem>
    <MenuItem value="option2">Option 2</MenuItem>
    <MenuItem value="option3">Option 3</MenuItem>
  </Select>
</FormControl>`}
            >
              <FormControl fullWidth>
                <InputLabel>Choose Option</InputLabel>
                <Select
                  value={selectValue}
                  label="Choose Option"
                  onChange={(e) => setSelectValue(e.target.value)}
                >
                  <MenuItem value="option1">Option 1</MenuItem>
                  <MenuItem value="option2">Option 2</MenuItem>
                  <MenuItem value="option3">Option 3</MenuItem>
                </Select>
              </FormControl>
            </ComponentShowcase>

            <ComponentShowcase
              title="Switch, Checkbox, Radio"
              description="Toggle controls with theme colors."
              code={`<Switch checked={switchChecked} onChange={(e) => setSwitchChecked(e.target.checked)} />
<Checkbox checked={checkboxChecked} onChange={(e) => setCheckboxChecked(e.target.checked)} />
<RadioGroup value={radioValue} onChange={(e) => setRadioValue(e.target.value)}>
  <FormControlLabel value="option1" control={<Radio />} label="Option 1" />
  <FormControlLabel value="option2" control={<Radio />} label="Option 2" />
</RadioGroup>`}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" gutterBottom>
                    Switch
                  </Typography>
                  <Switch
                    checked={switchChecked}
                    onChange={(e) => setSwitchChecked(e.target.checked)}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" gutterBottom>
                    Checkbox
                  </Typography>
                  <Checkbox
                    checked={checkboxChecked}
                    onChange={(e) => setCheckboxChecked(e.target.checked)}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" gutterBottom>
                    Radio Group
                  </Typography>
                  <RadioGroup value={radioValue} onChange={(e) => setRadioValue(e.target.value)}>
                    <FormControlLabel value="option1" control={<Radio />} label="Option 1" />
                    <FormControlLabel value="option2" control={<Radio />} label="Option 2" />
                    <FormControlLabel value="option3" control={<Radio />} label="Option 3" />
                  </RadioGroup>
                </Box>
              </Stack>
            </ComponentShowcase>

            <ComponentShowcase
              title="Slider"
              description="Slider for numeric input with primary color."
              code={`<Slider
  value={sliderValue}
  onChange={(_, value) => setSliderValue(value as number)}
  min={0}
  max={100}
  valueLabelDisplay="auto"
/>`}
            >
              <Box sx={{ px: 2 }}>
                <Slider
                  value={sliderValue}
                  onChange={(_, value) => setSliderValue(value as number)}
                  min={0}
                  max={100}
                  valueLabelDisplay="auto"
                />
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Value: {sliderValue}
                </Typography>
              </Box>
            </ComponentShowcase>
          </Box>
        </TabPanel>

        {/* Feedback Tab */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ px: 3 }}>
            <ComponentShowcase
              title="Alerts"
              description="Alert components for different message types."
              code={`<Alert severity="success">Success message</Alert>
<Alert severity="info">Info message</Alert>
<Alert severity="warning">Warning message</Alert>
<Alert severity="error">Error message</Alert>`}
            >
              <Stack spacing={2}>
                <Alert severity="success" icon={<CheckCircleOutline />}>
                  Success - Operation completed successfully!
                </Alert>
                <Alert severity="info" icon={<InfoOutlined />}>
                  Info - Here's some useful information.
                </Alert>
                <Alert severity="warning" icon={<WarningAmberOutlined />}>
                  Warning - Please be careful with this action.
                </Alert>
                <Alert severity="error" icon={<ErrorOutline />}>
                  Error - Something went wrong. Please try again.
                </Alert>
              </Stack>
            </ComponentShowcase>

            <ComponentShowcase
              title="Progress Indicators"
              description="Loading and progress indicators with primary color."
              code={`<CircularProgress />
<CircularProgress size={24} />
<LinearProgress />
<LinearProgress variant="determinate" value={sliderValue} />`}
            >
              <Stack spacing={3}>
                <Box>
                  <Typography variant="body2" gutterBottom>
                    Circular Progress
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <CircularProgress />
                    <CircularProgress size={24} />
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="body2" gutterBottom>
                    Linear Progress
                  </Typography>
                  <Stack spacing={1}>
                    <LinearProgress />
                    <LinearProgress variant="determinate" value={sliderValue} />
                    <Typography variant="caption" color="text.secondary">
                      {sliderValue}% complete
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </ComponentShowcase>

            <ComponentShowcase
              title="Chips"
              description="Chip components for tags and status indicators."
              code={`<Chip label="Default" />
<Chip label="Primary" color="primary" />
<Chip label="Success" color="success" />
<Chip label="Error" color="error" />
<Chip label="Deletable" onDelete={() => {}} />`}
            >
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                <Chip label="Default" />
                <Chip label="Primary" color="primary" />
                <Chip label="Success" color="success" />
                <Chip label="Error" color="error" />
                <Chip label="Warning" color="warning" />
                <Chip label="Info" color="info" />
                <Chip label="With Icon" icon={<CheckCircleOutline />} />
                <Chip label="Deletable" onDelete={() => alert('Delete clicked')} />
              </Stack>
            </ComponentShowcase>

            <ComponentShowcase
              title="Badge & Avatar"
              description="Badge for notifications and Avatar for user profiles."
              code={`<Badge badgeContent={4} color="error">
  <NotificationsOutlined />
</Badge>
<Avatar>JD</Avatar>
<Avatar sx={{ bgcolor: 'secondary.main' }}>AS</Avatar>`}
            >
              <Stack direction="row" spacing={3} alignItems="center">
                <Badge badgeContent={4} color="error">
                  <NotificationsOutlined />
                </Badge>
                <Badge badgeContent={99} color="primary">
                  <NotificationsOutlined />
                </Badge>
                <Avatar>JD</Avatar>
                <Avatar sx={{ bgcolor: 'secondary.main' }}>AS</Avatar>
                <Avatar sx={{ bgcolor: 'success.main' }}>BJ</Avatar>
              </Stack>
            </ComponentShowcase>
          </Box>
        </TabPanel>

        {/* Tables & Data Tab */}
        <TabPanel value={activeTab} index={3}>
          <Box sx={{ px: 3 }}>
            <ComponentShowcase
              title="DataGrid Pro"
              description="Advanced data grid with sorting, filtering, and pagination. Uses StyledDataGridWrapper component."
              code={`import { StyledDataGridWrapper } from '@/components/StyledDataGrid';

const columns: GridColDef[] = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
  { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
  { field: 'role', headerName: 'Role', width: 120 },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    renderCell: (params) => (
      <Chip
        label={params.value}
        size="small"
        color={params.value === 'active' ? 'success' : 'default'}
      />
    ),
  },
];

<StyledDataGridWrapper
  rows={sampleRows}
  columns={columns}
  height={300}
/>`}
            >
              <StyledDataGridWrapper
                rows={sampleRows}
                columns={sampleColumns}
                height={300}
              />
            </ComponentShowcase>

            <ComponentShowcase
              title="Table with Header Filters"
              description="Reusable table component with filter inputs directly in column headers - best UX practice. Used throughout the application (Insights page, etc). Clean, lightweight alternative to DataGrid with full filtering, sorting, and pagination."
              code={`import { FilterableTable, FilterableColumn } from '@/components/FilterableTable';
import { Chip, Typography } from '@mui/material';

// Define columns with custom render functions
const columns: FilterableColumn[] = [
  { field: 'name', label: 'Name', minWidth: 150 },
  { field: 'email', label: 'Email', minWidth: 200 },
  { field: 'role', label: 'Role', width: 140 },
  { field: 'department', label: 'Department', width: 140 },
  {
    field: 'status',
    label: 'Status',
    width: 120,
    render: (value) => (
      <Chip
        label={value}
        size="small"
        color={value === 'active' ? 'success' : 'default'}
        sx={{ textTransform: 'capitalize' }}
      />
    ),
  },
  {
    field: 'salary',
    label: 'Salary',
    width: 120,
    align: 'right',
    render: (value) => (
      <Typography variant="body2" fontWeight={500}>
        \${value?.toLocaleString()}
      </Typography>
    ),
  },
];

// Use the table
<FilterableTable
  columns={columns}
  data={employeeData}
  defaultRowsPerPage={25}
  rowsPerPageOptions={[10, 25, 50, 100]}
  defaultOrderBy="name"
  defaultOrder="asc"
/>

// Features:
// - Filter inputs integrated directly in table headers (always visible)
// - Real-time filtering with case-insensitive text search
// - Sortable columns with sort indicators
// - Pagination with customizable options
// - Custom render functions for any column (chips, formatted numbers, etc)
// - Responsive column widths (width/minWidth)
// - Sticky headers for long tables
// - Clean card-style wrapper with shadow
// - Built using StyledTable components for consistency
// - TypeScript support with generic types`}
            >
              <FilterableTable
                columns={filterableTableColumns}
                data={filterableTableData}
                defaultRowsPerPage={5}
                rowsPerPageOptions={[5, 10, 25]}
                defaultOrderBy="name"
                defaultOrder="asc"
                maxHeight={450}
                getRowKey={(row) => row.id}
              />
            </ComponentShowcase>

            <ComponentShowcase
              title="Expandable/Nested Table"
              description="Table with expandable rows showing nested data. Click the arrow to expand/collapse detail rows. This is the pattern used in the Insights page."
              code={`import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableBody,
  Table,
  TableCell,
  TableRow,
} from '@/components/StyledTable';

const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

const handleToggle = (id: number) => {
  const newExpanded = new Set(expandedRows);
  if (newExpanded.has(id)) {
    newExpanded.delete(id);
  } else {
    newExpanded.add(id);
  }
  setExpandedRows(newExpanded);
};

// Wrap table in white card with shadow
<Box
  sx={{
    backgroundColor: '#FFFFFF',
    borderRadius: '10px',
    boxShadow: '0 10px 30px rgba(146,153,184,0.15)',
    overflow: 'hidden',
  }}
>
  <StyledTableContainer sx={{ maxHeight: 600 }}>
    <Table stickyHeader>
      <StyledTableHead>
        <TableRow>
          <TableCell sx={{ width: 50 }} />
          <TableCell sx={{ width: 250 }}>Campaign</TableCell>
          <TableCell align="right" sx={{ width: 100 }}>Spend</TableCell>
          <TableCell align="right" sx={{ width: 100 }}>Revenue</TableCell>
        </TableRow>
      </StyledTableHead>
    <StyledTableBody>
      {campaigns.map((row) => {
        const isExpanded = expandedRows.has(row.id);
        return (
          <React.Fragment key={row.id}>
            <TableRow hover>
              <TableCell>
                <IconButton size="small" onClick={() => handleToggle(row.id)}>
                  {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>
              </TableCell>
              <TableCell>{row.name}</TableCell>
              <TableCell align="right">$[row.spend]</TableCell>
              <TableCell align="right">$[row.revenue]</TableCell>
            </TableRow>

            {/* Nested Detail Rows - use className="nested-row" */}
            {isExpanded && row.details.map((detail) => (
              <TableRow key={detail.id} className="nested-row">
                <TableCell />
                <TableCell sx={{ pl: 6 }}>{detail.name}</TableCell>
                <TableCell align="right">$[detail.spend]</TableCell>
                <TableCell align="right">$[detail.revenue]</TableCell>
              </TableRow>
            ))}
          </React.Fragment>
        );
      })}
    </StyledTableBody>
  </Table>
  </StyledTableContainer>
</Box>

// Key Points:
// - Wrap in white Box with shadow for card effect
// - Use StyledTableHead for gray header with uppercase text
// - Set specific widths on TableCell for consistent columns
// - Use stickyHeader on Table for fixed headers when scrolling`}
            >
              <Box
                sx={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '10px',
                  boxShadow: '0 10px 30px rgba(146,153,184,0.15)',
                  overflow: 'hidden',
                }}
              >
                <StyledTableContainer sx={{ maxHeight: 400 }}>
                  <Table stickyHeader>
                    <StyledTableHead>
                      <TableRow>
                        <TableCell sx={{ width: 50 }} />
                        <TableCell sx={{ width: 250 }}>Campaign</TableCell>
                        <TableCell align="right" sx={{ width: 100 }}>Spend</TableCell>
                        <TableCell align="right" sx={{ width: 100 }}>Revenue</TableCell>
                        <TableCell align="right" sx={{ width: 80 }}>ROAS</TableCell>
                      </TableRow>
                    </StyledTableHead>
                    <StyledTableBody>
                      {[
                        {
                          id: 1,
                          name: 'Summer Sale 2025',
                          spend: 1250.0,
                          revenue: 2100.0,
                          roas: 1.68,
                          details: [
                            { id: 11, date: 'Jan 15', spend: 250, revenue: 420, roas: 1.68 },
                            { id: 12, date: 'Jan 16', spend: 250, revenue: 420, roas: 1.68 },
                            { id: 13, date: 'Jan 17', spend: 250, revenue: 420, roas: 1.68 },
                          ],
                        },
                        {
                          id: 2,
                          name: 'Q1 Brand Awareness',
                          spend: 850.0,
                          revenue: 1190.0,
                          roas: 1.4,
                          details: [
                            { id: 21, date: 'Jan 15', spend: 283.33, revenue: 396.67, roas: 1.4 },
                            { id: 22, date: 'Jan 16', spend: 283.33, revenue: 396.67, roas: 1.4 },
                          ],
                        },
                        {
                          id: 3,
                          name: 'Product Launch Campaign',
                          spend: 2100.0,
                          revenue: 3150.0,
                          roas: 1.5,
                          details: [
                            { id: 31, date: 'Jan 15', spend: 700, revenue: 1050, roas: 1.5 },
                            { id: 32, date: 'Jan 16', spend: 700, revenue: 1050, roas: 1.5 },
                            { id: 33, date: 'Jan 17', spend: 700, revenue: 1050, roas: 1.5 },
                          ],
                        },
                      ].map((campaign) => {
                        const isExpanded = expandedTableRows.has(campaign.id);
                        return (
                          <React.Fragment key={campaign.id}>
                            <TableRow hover>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleTableRow(campaign.id)}
                                >
                                  {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>
                                  {campaign.name}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">${campaign.spend.toFixed(2)}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">${campaign.revenue.toFixed(2)}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={500}>
                                  {(campaign.roas * 100).toFixed(0)}%
                                </Typography>
                              </TableCell>
                            </TableRow>

                            {isExpanded &&
                              campaign.details.map((detail) => (
                                <TableRow
                                  key={detail.id}
                                  className="nested-row"
                                >
                                  <TableCell />
                                  <TableCell sx={{ pl: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      {detail.date}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" fontSize="12px">
                                      ${detail.spend.toFixed(2)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" fontSize="12px">
                                      ${detail.revenue.toFixed(2)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" fontSize="12px">
                                      {(detail.roas * 100).toFixed(0)}%
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </React.Fragment>
                        );
                      })}
                    </StyledTableBody>
                  </Table>
                </StyledTableContainer>
              </Box>
            </ComponentShowcase>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              FilterableTable Best Practices
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Lightweight table with header filters - best UX practices for data filtering:
            </Typography>
            <Stack component="ul" spacing={1} sx={{ pl: 3, mb: 4 }}>
              <Typography component="li" variant="body2">
                <strong>Always Visible Filters:</strong> Filter inputs integrated directly in table headers - no toggle needed, always accessible
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Real-time Filtering:</strong> Instant filtering as you type using useMemo for optimal performance
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Case Insensitive:</strong> All text searches use .toLowerCase() for better user experience
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Custom Render Functions:</strong> Use render prop to display chips, formatted numbers, icons, or any custom UI
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Column Configuration:</strong> Set width/minWidth for fixed or flexible columns, align for text alignment
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Sorting Built-in:</strong> Click column headers to sort (set sortable: false to disable for specific columns)
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Pagination:</strong> Customize defaultRowsPerPage and rowsPerPageOptions to fit your data size
              </Typography>
              <Typography component="li" variant="body2">
                <strong>TypeScript Support:</strong> Generic type support for type-safe column definitions and data
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Consistent Styling:</strong> Uses StyledTable components to match the rest of the application
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Lightweight:</strong> No DataGrid Pro dependency - built with basic MUI Table components
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Filter Input Height:</strong> Standardized 32px height matching project standards
              </Typography>
            </Stack>

            <Typography variant="h6" gutterBottom>
              Table Implementation Notes
            </Typography>
            <Stack component="ul" spacing={1} sx={{ pl: 3, mb: 4 }}>
              <Typography component="li" variant="body2">
                <strong>Use StyledTableHead:</strong> Our custom table header with #F4F5F7 background, uppercase text, and proper styling
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Card Wrapper:</strong> Wrap table in white Box with borderRadius: 10px and boxShadow for card effect
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Theme-First Approach:</strong> Use className="nested-row" for nested rows - no inline styles needed
              </Typography>
              <Typography component="li" variant="body2">
                <strong>No Collapse Component:</strong> Render nested rows directly as TableRow elements
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Automatic Styling:</strong> nested-row class applies #F8F9FA background, #F1F3F4 hover, and 12px font
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Column Widths:</strong> Set sx=&#123;&#123; width: X &#125;&#125; on TableCell in header for consistent columns
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Sticky Headers:</strong> Use stickyHeader prop on Table for fixed headers when scrolling
              </Typography>
              <Typography component="li" variant="body2">
                <strong>React.Fragment:</strong> Wrap main and detail rows in Fragment to keep them together
              </Typography>
            </Stack>

            <Typography variant="h6" gutterBottom>
              DataGrid Features
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Our DataGrid implementation includes:
            </Typography>
            <Stack component="ul" spacing={1} sx={{ pl: 3 }}>
              <Typography component="li" variant="body2">
                <strong>Styled Headers:</strong> Gray background (#F4F5F7), uppercase text, 52px height
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Consistent Rows:</strong> All rows have uniform 52px height with centered content
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Hover Effects:</strong> Rows highlight on hover with primary color tint
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Transparent Icons:</strong> Sort and action icons have no background
              </Typography>
              <Typography component="li" variant="body2">
                <strong>PRO License:</strong> We have DataGrid Pro license for advanced features
              </Typography>
            </Stack>
          </Box>
        </TabPanel>

        {/* Custom Components Tab */}
        <TabPanel value={activeTab} index={4}>
          <Box sx={{ px: 3 }}>
            <Typography variant="h6" gutterBottom>
              Custom Reusable Components
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              These are project-specific components created for consistency and reusability.
            </Typography>

            <ComponentShowcase
              title="PageHeader"
              description="Standard page header with title and subtitle."
              code={`import { PageHeader } from '@/components/common';

<PageHeader
  title="Page Title"
  subtitle="Page description or subtitle"
/>`}
            >
              <PageHeader
                title="Example Page Title"
                subtitle="This is a subtitle that describes the page"
              />
            </ComponentShowcase>

            <ComponentShowcase
              title="ContentCard"
              description="White card container with rounded corners for content sections."
              code={`import { ContentCard } from '@/components/common';

<ContentCard>
  <Typography>Card content goes here</Typography>
</ContentCard>`}
            >
              <ContentCard>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Card Title
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This is a content card. It provides a white background with consistent styling.
                  </Typography>
                </Box>
              </ContentCard>
            </ComponentShowcase>

            <ComponentShowcase
              title="SplitLayout"
              description="Responsive layout with configurable main/sidebar split (default 75/25)."
              code={`import { SplitLayout } from '@/components/common';

<SplitLayout
  mainWidth={75}
  gap={3}
  main={<ContentCard>Main content</ContentCard>}
  sidebar={<ContentCard>Sidebar content</ContentCard>}
/>`}
            >
              <Typography variant="body2" color="text.secondary">
                See Studio page for live example. Creates elastic 75%/25% layout that scales with screen size.
              </Typography>
            </ComponentShowcase>

            <ComponentShowcase
              title="RequiredLabel"
              description="Form label with red asterisk for required fields."
              code={`import { RequiredLabel } from '@/components/common';

<TextField
  label={<RequiredLabel>Email</RequiredLabel>}
  fullWidth
/>`}
            >
              <TextField
                label={<RequiredLabel>Email Address</RequiredLabel>}
                fullWidth
                placeholder="user@example.com"
              />
            </ComponentShowcase>

            <ComponentShowcase
              title="EmptyState"
              description="Consistent empty state display with icon and message."
              code={`import { EmptyState } from '@/components/common';
import { FolderOutlined } from '@mui/icons-material';

<EmptyState
  icon={<FolderOutlined />}
  message="No items found"
/>`}
            >
              <Typography variant="body2" color="text.secondary">
                See Generated Assets panel in Studio page for live example.
              </Typography>
            </ComponentShowcase>
          </Box>
        </TabPanel>

        {/* Theme & Colors Tab */}
        <TabPanel value={activeTab} index={5}>
          <Box sx={{ px: 3 }}>
            <Typography variant="h6" gutterBottom>
              Theme Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              All theme configuration is centralized in <code>src/theme/index.ts</code>
            </Typography>

            <ComponentShowcase
              title="Color Palette"
              description="Primary and secondary colors with success, warning, error, and info variants."
              code={`// Theme colors
primary: '#4CAEB8'    // Teal/Cyan
secondary: '#FBB24C'  // Orange/Gold
success: '#20C997'    // Green
warning: '#FA8B0C'    // Orange
error: '#FF4D4F'      // Red
info: '#2C99FF'       // Blue`}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Primary & Secondary
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Box
                      sx={{
                        width: 100,
                        height: 60,
                        bgcolor: '#4CAEB8',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    >
                      Primary
                    </Box>
                    <Box
                      sx={{
                        width: 100,
                        height: 60,
                        bgcolor: '#FBB24C',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    >
                      Secondary
                    </Box>
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Status Colors
                  </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                    <Box
                      sx={{
                        width: 100,
                        height: 60,
                        bgcolor: '#20C997',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    >
                      Success
                    </Box>
                    <Box
                      sx={{
                        width: 100,
                        height: 60,
                        bgcolor: '#FA8B0C',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    >
                      Warning
                    </Box>
                    <Box
                      sx={{
                        width: 100,
                        height: 60,
                        bgcolor: '#FF4D4F',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    >
                      Error
                    </Box>
                    <Box
                      sx={{
                        width: 100,
                        height: 60,
                        bgcolor: '#2C99FF',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    >
                      Info
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </ComponentShowcase>

            <ComponentShowcase
              title="Typography"
              description="Font family: Inter, with consistent heading and body sizes."
              code={`<Typography variant="h1">Heading 1</Typography>
<Typography variant="h2">Heading 2</Typography>
<Typography variant="h3">Heading 3</Typography>
<Typography variant="body1">Body 1</Typography>
<Typography variant="body2">Body 2</Typography>
<Typography variant="caption">Caption</Typography>`}
            >
              <Stack spacing={2}>
                <Typography variant="h1">Heading 1 - 2.5rem</Typography>
                <Typography variant="h2">Heading 2 - 2rem</Typography>
                <Typography variant="h3">Heading 3 - 1.75rem</Typography>
                <Typography variant="h4">Heading 4 - 1.5rem</Typography>
                <Typography variant="h5">Heading 5 - 1.25rem</Typography>
                <Typography variant="h6">Heading 6 - 1rem</Typography>
                <Typography variant="body1">Body 1 - 0.875rem (default text)</Typography>
                <Typography variant="body2">Body 2 - 0.8125rem (smaller text)</Typography>
                <Typography variant="caption">Caption - For small labels and hints</Typography>
              </Stack>
            </ComponentShowcase>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Best Practices
            </Typography>
            <Stack component="ul" spacing={1} sx={{ pl: 3 }}>
              <Typography component="li" variant="body2">
                <strong>Theme-First Approach:</strong> Configure styling in theme.ts, not inline styles
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Component Extraction:</strong> Create separate components for complex patterns
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Reusability:</strong> Use common components (PageHeader, ContentCard, etc.) consistently
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Color Usage:</strong> Always use theme palette colors, never hardcoded hex values
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Spacing:</strong> Use theme spacing units (sx=&#123;&#123; p: 3, m: 2 &#125;&#125;) for consistency
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Responsive:</strong> Use percentage-based widths and flex layouts for elastic design
              </Typography>
            </Stack>
          </Box>
        </TabPanel>
      </ContentCard>
    </Container>
  );
}

export default DevelopersPage;
